import Image from 'next/image'
import type {
	Elements,
	ElementModels,
	IContentItem,
} from '@kontent-ai/delivery-sdk'
import type {ReactElement} from 'react'
import {
	Icon as CIcon,
	type IconSize,
} from '@/products/flexible-web-toolkit/components/icon'
import {loadImageFromKontentAI} from '@/image-loader'
import type {Block} from '@/services/kontent-ai/types'
import type {IconWithTextAndCtaContentItem} from '@/products/flexible-web-toolkit/blocks/icon-with-text-and-cta'
import {StatsIcons} from '@/products/flexible-web-toolkit/components/stats-icon'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'
import {ReusableCTAButtonComponent} from '../cta-button'
import {spacing} from '../../styles'

export type SmallBenefitsBlockContentItem = IContentItem<{
	iconWithTextAndCta: Elements.LinkedItemsElement<IconWithTextAndCtaContentItem>
	borderOption: Elements.MultipleChoiceElement
	snippetSpacingSpacing: Elements.MultipleChoiceElement
	backgroundOption: Elements.MultipleChoiceElement
}>

const SmallBenefitIcon = ({
	block: icon,
	page,
	borderOption,
	backgroundOption,
	...context
}: Parameters<Block<IconWithTextAndCtaContentItem>>[0] & {
	borderOption: ElementModels.MultipleChoiceOption[]
	backgroundOption: ElementModels.MultipleChoiceOption[]
}): ReactElement => {
	const {colorId: iconBrandColor} = colorWithFallback({
		blockColor: icon.elements.brandColor,
		blockColorLegacy: icon.elements.brand,
		pageConfig: page.elements.config[0],
		fallbackColorId: 'primary',
	})

	return (
		<div data-kontent-item-id={icon.system.id} key={icon.system.id}>
			<CIcon
				brand={iconBrandColor ?? undefined}
				noBackground={backgroundOption[0]?.codename === 'yes'}
				noBorder={borderOption[0]?.codename !== 'yes'}
				size={(icon.elements.size[0]?.codename || 'lg') as IconSize}
				title={
					<span
						className={
							icon.elements.boldTitle[0]?.codename === 'bold'
								? 'font-bold'
								: undefined
						}
						data-kontent-element-codename="title"
					>
						{icon.elements.title}
					</span>
				}
			>
				<div className="relative h-full w-full">
					<Image
						alt={icon.elements.icon[0]?.description ?? ''}
						data-kontent-element-codename="icon"
						fill
						loader={loadImageFromKontentAI}
						src={
							icon.elements.icon[0]?.url ??
							'https://assets-eu-01.kc-usercontent.com/43db687c-fb47-015f-c990-00802940e2bc/f03522f9-0615-4ca8-a83c-3eb311fd2159/placeholder-256x176.png'
						}
					/>
				</div>
			</CIcon>
			<div
				dangerouslySetInnerHTML={{
					__html: icon.elements.content.value,
				}}
				data-kontent-element-codename="content"
			/>
			{icon.elements.buttons.map((button) => (
				<ReusableCTAButtonComponent
					block={button}
					className="mt-6"
					key={button.system.id}
					page={page}
					{...context}
				/>
			))}
		</div>
	)
}

export const SmallBenefitsBlockBlock: Block<SmallBenefitsBlockContentItem> = ({
	block,
	page,
	...context
}) => {
	const spacingChoice =
		spacing[
			block.elements.snippetSpacingSpacing[0]
				?.codename as keyof typeof spacing
		]
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- not unnecessary, spacingChoice can be undefined
	const componentSpacing = !spacingChoice ? spacing.default : spacingChoice

	return (
		<div className={`container-wide ${componentSpacing}`}>
			<StatsIcons className="px-2" col={2} grayBG={false} hasNoFlexWrap>
				{block.elements.iconWithTextAndCta.map((icon) => (
					<SmallBenefitIcon
						backgroundOption={block.elements.backgroundOption}
						block={icon}
						borderOption={block.elements.borderOption}
						key={icon.system.id}
						page={page}
						{...context}
					/>
				))}
			</StatsIcons>
		</div>
	)
}
